import { AgeGroup } from 'models/ageGroups';
import { IntlShape, useIntl } from 'react-intl';
import { SelectOption as Option } from 'components/select/types';
import { useMemo } from 'react';
import { useAppStore } from 'hooks/state/useAppStore';
import { BaseSelect } from 'components/BaseSelect';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';

interface AgeGroupSelectProps {
  selectedAgeGroups?: string[];
  setSelectedAgeGroups?: (selectedAgeGroups: string[]) => void;
  className?: string;
  showConfirmButton?: boolean;
}

const mapAgeGroupToAgeGroupOption = (ageGroup: AgeGroup, intl: IntlShape): Option<string> => ({
  value: ageGroup.ageGroup,
  label: intl.formatMessage({ id: `product.ageGroup.${ageGroup.ageGroup}`, defaultMessage: ageGroup.ageGroup }),
});

export function AgeGroupSelect({
  selectedAgeGroups = undefined,
  setSelectedAgeGroups = undefined,
  className = undefined,
  showConfirmButton = false,
}: AgeGroupSelectProps) {
  const intl = useIntl();

  const { allAgeGroups, disabledFilters } = useTransitiveCacheStore();

  const allAgeGroupOptions = useMemo(
    () =>
      allAgeGroups
        .map(ag => mapAgeGroupToAgeGroupOption(ag, intl))
        .sort((a, b) => {
          if (a.label && b.label) {
            return a.label.localeCompare(b.label);
          }

          return 0;
        }),
    [allAgeGroups, intl]
  );

  const { ageGroups, setAgeGroups } = useAppStore();

  return (
    <BaseSelect
      disabled={disabledFilters.includes('prdTargetAge')}
      id="age-group-filter"
      className={className}
      label={intl.formatMessage({
        id: 'filter.key.selectedAgeGroups',
        defaultMessage: 'Age Group',
      })}
      selectedItems={selectedAgeGroups ?? ageGroups ?? []}
      allOptionItems={allAgeGroupOptions}
      setSelectedItems={setSelectedAgeGroups ?? setAgeGroups}
      showConfirmButton={showConfirmButton}
    />
  );
}
